import AlcoholCalcNavbar from "../Navbars/AlcoholCalcNavbar";
import React, {useState} from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {getCookie} from "../../tools/tools";
import Plot from 'react-plotly.js';


const AlcoholCalcV1 = () => {
    const [formData, setFormData] = useState({
        estimated_parameter: 'BAC',
        V: 500,
        M: 100,
        z: 0.4,
        t: 2,
        Ct: 0,
        r: 0.7,
        b: 0.15,
        a: 2,
        d: 0.3,
        eV: 20,
        eM: 1,
        ez: 0.01,
        et: 0.25,
        eC: 0.01,
        er: 0.1,
        eb: 0.025,
        ea: 1,
        ed: 0.05,
    });
    const [plotData, setPlotData] = useState(null);
    const names = {
        estimated_parameter: 'BAC',
        'V': 'Volume of the drink, ml',
        'z': 'Strength of the drink, d.u.',
        'M': 'Mass of the subject, kg',
        't': 'Time after alcohol intake, hours',
        'Ct': 'BAC at time t (C), g/kg or g/l',
        'r': 'Reduction factor, d.u. or l/kg',
        'b': 'Elimination rate (β), g/kg/h or g/l/h',
        'a': 'Absorption constant, 1/h',
        'd': 'Extraction constant, 1/h',
    }
    const requiredFields = formData.estimated_parameter === 'BAC'
        ? ['V', 'z', 'M', 't', 'r', 'b', 'a', 'd']
        : ['Ct', 'M', 't', 'r', 'b', 'a', 'd'];

    const [result, setResult] = useState(null);
    const [errors, setErrors] = useState([]);
    const validationRules = {
      V: { min: 0, max: 1e10 },
      eV: { min: 0, max: 1e10 },
      z: { min: 0, max: 1 },
      ez: { min: 0, max: 1 },
      M: { min: 1e-10, max: 1e10 },
      eM: { min: 0, max: 1e10 },
      t: { min: 1e-10, max: 1e10},
      et: { min: 0, max: 1e10},
      Ct: { min: 0, max: 1e10 },
      eC: { min: 0, max: 1e10},
      r: { min: 1e-10, max: 1,},
      er: { min: 0, max: 1 },
      b: { min: 1e-10, max: 1e10},
      eb: { min: 0, max: 0.1 },
      a: { min: 1e-10, max: 8 },
      ea: { min: 0, max: 8},
      d: { min: 1e-10, max: 0.6 },
      ed: { min: 0, max: 0.6 }
    };

    const [isValidForm, setIsValidForm] = useState(true);
    const checkFormValidity = () => {
      const allRequiredFieldsFilled = requiredFields.every(field => {
        const value = formData[field];
        return value !== '' && value !== null && value !== undefined;
      });

      const hasNoErrors = Object.keys(errors).length === 0;

      return allRequiredFieldsFilled && hasNoErrors;
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        const numericValue = parseFloat(value);
        const rules = validationRules[name];
        const newErrors = {};
        setFormData({...formData, [name]: value});
        if (isNaN(numericValue) && name != 'estimated_parameter')
            newErrors[name] = `Value of ${names[name]}  must be float`;
        else if (rules) {
            if (typeof rules.min === 'number' && numericValue < rules.min) {
                newErrors[name] = `Value of ${names[name]} must be more or equal ${rules.min}`;
            } else if (typeof rules.max === 'number' && numericValue > rules.max) {
                newErrors[name] = `Value of ${names[name]}  must be less or equal ${rules.max}`;
            } else {
                delete newErrors[name];
            }
        }
        if (!newErrors[name]) {
            if(errors[name])
                delete errors[name];
            if(checkFormValidity())
                setIsValidForm(true);
        }
        else {
            setErrors(prevErrors => ({...prevErrors, ...newErrors}));
            setIsValidForm(false);
        }
    };
    const handleSubmit = async () => {
        try {
            const response = await axios.post('/api/alcohol-calcs/1', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'X-CSRFToken': getCookie('csrftoken')
                },
                withCredentials: true,
            });
            if (response.data.errors && response.data.errors.length > 0) {
                setErrors(response.data.errors);
                setResult(null);
                setPlotData(null);
            }
            else
                {
                    setErrors([]);
                    setPlotData(null);
                    setResult(response.data);
                    console.log(response.data);
                    if (response.data.points)
                        setPlotData({
                            x: response.data.points.x,
                            y: response.data.points.y,
                            config: response.data.points.config
                        });

                }
        } catch (error) {
            console.error('Error occurred:', error);
            setErrors([]);
            setPlotData(null);
            setResult(null);
            setErrors(['An error occurred while processing the request']);
        }
    };
    const formatResults = () => {
        if (!result) return [];

        let params = ['C', 'Cmax', 'tmax', 't12', 'tf',];
        if (result[params[0]] === undefined)
            params = ['m'];
        return params.map((param) => ({
            name: param === 't12' ? <span>t<sub>a0,5</sub></span> : param,
            value: result[param],
            error: result['d' + param] || 0,
            lower: result['l' + param] ?? 0,
            upper: param === 'C' ? result['u' + param] : (result[param] || 0) + (result['d' + param] || 0)
        }));
    };

    return (
        <div className="content">
            <AlcoholCalcNavbar/>
            <div className="section section-components" style={{paddingTop: 40}}>
                <div className="container mb-5 pb-100">
                    <h1 className="h1 text-success font-weight-bold mb-4">Calculator of Ethanol Blood Kinetics Post
                        Single Oral Alcohol Intake </h1>
                    <h2 className="text-muted">Input parameter</h2>
                    <div className="form-group row">
                        <label className="col-sm-4 col-form-label">Estimated parameter:</label>
                        <div className="col-sm-8">
                            <label style={{display: "flex", alignItems: "center"}}>
                                <input
                                    type="radio"
                                    name="estimated_parameter"
                                    value="BAC"
                                    checked={formData.estimated_parameter === 'BAC'}
                                    onChange={handleInputChange}
                                />
                                <span style={{fontSize: 15, paddingLeft: 10}}>BAC</span>
                            </label>
                            <label style={{display: "flex", alignItems: "center"}}>
                                <input
                                    type="radio"
                                    name="estimated_parameter"
                                    value="Amount of alcohol"
                                    checked={formData.estimated_parameter === 'Amount of alcohol'}
                                    onChange={handleInputChange}
                                />
                                <span style={{fontSize: 15, paddingLeft: 10}}>Amount of alcohol</span>
                            </label>
                        </div>
                    </div>

                    {[["V", "eV"], ["z", "ez"], ["M", "eM"], ["t", "et"], ["Ct", "eC"], ["r", "er"], ["b", "eb"], ["a", "ea"], ["d", "ed"]].map((fields) => {
                        const isRequired = requiredFields.includes(fields[0]);
                        return (
                            <>
                            <div className={"form-group row"}>
                                <label className={'col-sm-4 col-form-label'}
                                       htmlFor={fields[0]}>{(names[fields[0]])}</label>
                                <input
                                    className="col-sm-4 form-control"
                                    key={fields[0]}
                                    type="number"
                                    name={fields[0]}
                                    value={formData[fields[0]]}
                                    onChange={handleInputChange}
                                    disabled={!isRequired}
                                />
                                <label className={'col-sm-1 col-form-label'} htmlFor={fields[1]}>+-</label>
                                <input
                                    className="col-sm-3 form-control"
                                    key={fields[1]}
                                    type="number"
                                    name={fields[1]}
                                    value={formData[fields[1]]}
                                    onChange={handleInputChange}
                                    disabled={!isRequired}
                                />
                            </div>
                                <div className={"form-group row"}>
                                    {errors[fields[0]] && (
                                        <label className={'col-sm-4 col-form-label text-danger'}>{errors[fields[0]]}</label>
                                    )}
                                    {errors[fields[1]] && (
                                        <label className={'col-sm-4 col-form-label text-danger'}>{errors[fields[1]]}</label>
                                    )}

                                </div>
                            </>
                        )
                    })}

                    <button onClick={handleSubmit} disabled={!isValidForm} className="btn btn-secondary mb-2">
                        Calculate
                    </button>
                    <div className="mt-2">
                        <small className="text-muted">
                            By clicking Calculate, you agree to the{' '}
                            <Link to="/agreement">User Agreement</Link>.
                        </small>
                    </div>
                    {result && (
                        <div className="mt-5">
                            <h2 className="text-muted mb-3">Calculation Results</h2>
                            <div className="table-responsive">
                                <table className="table table-striped">
                                    <thead>
                                    <tr>
                                        <th>Parameter</th>
                                        <th>Value</th>
                                        <th>Error</th>
                                        <th>Lower Bound</th>
                                        <th>Upper Bound</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {formatResults().map((param) => (
                                        <tr key={param.name}>
                                            <td><strong>{param.name}</strong></td>
                                            <td>{param.value.toFixed(6)}</td>
                                            <td>{param.error.toFixed(6)}</td>
                                            <td>{param.lower.toFixed(6)}</td>
                                            <td>{param.upper.toFixed(6)}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                            {plotData && (<div style={{width: '100%', height: '80vh'}}>
                                <Plot
                                    data={[
                                        {
                                            x: plotData.x,
                                            y: plotData.y,
                                            type: 'scatter',
                                            mode: 'lines',
                                            line: {color: '#2ca02c', width: 2},
                                            name:'BAC(t)'
                                        }
                                    ]}
                                    layout={{
                                        title: plotData.config?.title || 'Function of t',
                                        xaxis: {
                                            title: plotData.config?.x_label || 't',
                                            gridcolor: '#eee',
                                            zerolinecolor: '#ccc'
                                        },
                                        yaxis: {
                                            title: plotData.config?.y_label || 'C(t)',
                                            gridcolor: '#eee',
                                            zerolinecolor: '#ccc'
                                        },
                                        plot_bgcolor: '#f9f9f9',
                                        paper_bgcolor: '#f9f9f9',
                                        hovermode: 'closest',
                                        showlegend: true
                                    }}
                                    config={{
                                        responsive: true,
                                        displayModeBar: true,
                                        scrollZoom: true
                                    }}
                                    style={{width: '100%', height: '100%'}}
                                />
                            </div>)}
                        </div>
                    )}

                    {errors.length > 0 && (
                        <div className="alert alert-danger mt-4">
                            {errors.map((error, idx) => (
                                <div key={idx}>{error}</div>
                            ))}
                        </div>
                    )}

                </div>
            </div>
        </div>)
};

export default AlcoholCalcV1;